<template>
  <div class="practice-assistant-form">
    <div class="practice-assistant-form__step">
      <div class="practice-assistant-form__step-side">
        <div
          class="practice-assistant-form__step-node"
          :class="{
            'is-current': extractStatus === 'idle',
            'is-done': isStepOneFinished
          }"
        >
          <i v-if="isStepOneFinished" class="icon-checkbox-circle-line t-iconfont"></i>
          <span v-else>1</span>
        </div>
        <div class="practice-assistant-form__step-line"></div>
      </div>

      <div class="practice-assistant-form__step-main">
        <div class="practice-assistant-form__step-header">
          <div
            class="practice-assistant-form__step-title"
            :class="{
              'is-current': showStepOneBody
            }"
          >
            告诉 AI 想要什么
          </div>

          <!-- <i
            v-if="canToggleStepOne"
            class="practice-assistant-form__step-toggle t-iconfont"
            :class="stepOneExpanded ? 'icon-arrow-up-s-line' : 'icon-arrow-down-s-line'"
            @click="emit('toggle-step-one')"
          ></i> -->
        </div>

        <div v-show="showStepOneBody" class="practice-assistant-form__step-body">
          <div class="practice-assistant-form__field">
            <div class="practice-assistant-form__field-label">
              <span class="practice-assistant-form__required">*</span>
              <span>想要训练什么内容</span>
            </div>
            <el-input
              v-model="trainingContent"
              class="practice-assistant-form__textarea practice-assistant-form__textarea--prompt"
              :maxlength="2000"
              placeholder="请填写你这次想练习的沟通场景、沟通对象、希望达到的目标。

你可以这样写：
场景：销售与客户初次沟通采购需求；
对象：想要采购市政道路护栏的客户；
目标：通过本次电话的沟通了解客户的使用场景、项目地点、意向产品、预算、采购周期、采购量和历史采购情况。"
              :rows="10"
              show-word-limit
              type="textarea"
            />
          </div>

          <div class="practice-assistant-form__field">
            <div v-if="supplementFile.length" class="practice-assistant-form__upload-list">
              <UploadedFileCard
                v-for="(file, index) in supplementFile"
                :key="file.attachIndex || file.id || index"
                :file="file"
                @delete="handleDeleteUploadedFile"
              />
            </div>

            <div
              class="practice-assistant-form__upload-box"
              :class="{
                'is-disabled': uploadLoading || extractStatus === 'loading',
                'is-dragover': isDragOver
              }"
              @click="handleClickUpload"
              @dragleave.prevent="handleDragLeave"
              @dragover.prevent="handleDragOver"
              @drop.prevent="handleDrop"
            >
              <div class="practice-assistant-form__upload-action">
                <i class="icon-upload-line t-iconfont"></i>
                <span>拖拽或点击此处上传本地文件</span>
              </div>
              <div class="practice-assistant-form__upload-hint">
                支持 pdf、word、excel、markdown 格式，总文件大小上限为 {{ UPLOAD_SIZE_TEXT }}
              </div>
            </div>
            <div class="practice-assistant-form__upload-tip">
              可上传 SOP、产品手册、优秀话术等资料，AI
              会结合资料帮你生成更符合要求的陪练内容（非必填）
            </div>
            <input
              ref="uploadInputRef"
              :accept="UPLOAD_ACCEPT"
              hidden
              multiple
              type="file"
              @change="handleUploadInputChange"
            />
          </div>

          <el-button
            class="practice-assistant-form__step-btn"
            color="#7a52f4"
            :loading="extractStatus === 'loading'"
            @click="handleExtractClick"
          >
            {{ extractButtonText }}
          </el-button>
        </div>
      </div>
    </div>

    <div class="practice-assistant-form__step">
      <div class="practice-assistant-form__step-side">
        <div
          class="practice-assistant-form__step-node"
          :class="{
            'is-current': hasStepTwo && !isStepTwoFinished,
            'is-done': isStepTwoFinished,
            'is-wait': !hasStepTwo
          }"
        >
          <i v-if="isStepTwoFinished" class="icon-checkbox-circle-line t-iconfont"></i>
          <span v-else>2</span>
        </div>
      </div>

      <div class="practice-assistant-form__step-main">
        <div class="practice-assistant-form__step-header">
          <div
            class="practice-assistant-form__step-title"
            :class="{
              'is-current': hasStepTwo && !isStepTwoFinished
            }"
          >
            AI 理解并提炼生成
          </div>
        </div>

        <div v-if="showStepTwoLoading" class="practice-assistant-form__step-loading">
          <i class="icon-loader-2-line practice-assistant-form__loading-icon t-iconfont"></i>
          <span>正在思考分析中，提炼生成主题信息</span>
        </div>

        <div v-if="showStepTwoForm" class="practice-assistant-form__step-body">
          <div class="practice-assistant-form__field">
            <div class="practice-assistant-form__field-label">
              <span class="practice-assistant-form__required">*</span>
              <span>主题名称</span>
            </div>
            <div
              class="practice-assistant-form__field-content practice-assistant-form__field-content--half"
            >
              <el-input
                v-model="themeName"
                :maxlength="20"
                placeholder="请输入主题名称"
                show-word-limit
              />
            </div>
          </div>

          <div class="practice-assistant-form__field">
            <div class="practice-assistant-form__field-label">
              <span class="practice-assistant-form__required">*</span>
              <span>主题说明（包含场景描述与训练目标）</span>
            </div>
            <el-input
              v-model="themeDesc"
              class="practice-assistant-form__textarea practice-assistant-form__textarea--summary"
              :maxlength="2000"
              placeholder="可以继续补充场景描述、训练目标和重点关注内容。"
              :rows="6"
              show-word-limit
              type="textarea"
            />
          </div>

          <div class="practice-assistant-form__field">
            <div class="practice-assistant-form__field-label">
              <span class="practice-assistant-form__required">*</span>
              <span>本次任务派发给谁</span>
            </div>
            <div
              class="practice-assistant-form__field-content practice-assistant-form__field-content--half"
            >
              <PracticePersonSelect v-model:range-value="userRangePojoList" />
            </div>
          </div>

          <div class="practice-assistant-form__field">
            <div class="practice-assistant-form__field-label">
              <span class="practice-assistant-form__required">*</span>
              <span>任务截止时间</span>
            </div>
            <div
              class="practice-assistant-form__field-content practice-assistant-form__field-content--half"
            >
              <el-date-picker
                v-model="dueTime"
                class="practice-assistant-form__date-picker"
                :clearable="false"
                :disabled-date="disabledEndDate"
                format="YYYY-MM-DD HH:mm"
                placeholder="请选择任务截止时间"
                style="width: 100% !important"
                type="datetime"
                value-format="X"
              />
            </div>
          </div>

          <div class="practice-assistant-form__step-actions">
            <el-button class="practice-assistant-form__step-btn" @click="emit('prev-step')">
              返回上一步
            </el-button>
            <el-button
              class="practice-assistant-form__step-btn"
              color="#7a52f4"
              :loading="profileStatus === 'loading'"
              @click="emit('generate-profiles')"
            >
              {{ generateProfilesButtonText }}
            </el-button>
            <p class="practice-assistant-form__step-desc">
              预计耗时 1~3 分钟，生成完成后请选择要派发的客户画像
            </p>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showStepThree" class="practice-assistant-form__step">
      <div class="practice-assistant-form__step-side">
        <div
          class="practice-assistant-form__step-node"
          :class="{
            'is-current': hasStepThree,
            'is-wait': !hasStepThree
          }"
        >
          <span>3</span>
        </div>
      </div>

      <div class="practice-assistant-form__step-main">
        <div class="practice-assistant-form__step-header">
          <div
            class="practice-assistant-form__step-title"
            :class="{
              'is-current': hasStepThree
            }"
          >
            确认客户画像
          </div>
        </div>

        <div v-if="showStepThreeLoading" class="practice-assistant-form__step-loading">
          <i class="icon-loader-2-line practice-assistant-form__loading-icon t-iconfont"></i>
          <span>正在分析生成中，生成完成可直接派发或选择后派发</span>
        </div>

        <div v-if="showStepThreeForm" class="practice-assistant-form__step-body">
          <div class="practice-assistant-form__step-tips">
            取消勾选不适合本次训练的客户画像，需至少保留 1 个
          </div>

          <div class="practice-assistant-form__profile-list">
            <div
              v-for="profile in customerProfileList"
              :key="profile.id"
              class="practice-assistant-form__profile-card"
              :class="{
                'is-selected': isProfileSelected(profile.id)
              }"
            >
              <div class="practice-assistant-form__profile-header">
                <el-checkbox
                  :model-value="isProfileSelected(profile.id)"
                  @change="(checked) => handleProfileSelectionChange(profile, checked)"
                />
                <div class="practice-assistant-form__profile-info">
                  <div class="practice-assistant-form__profile-name">
                    {{ profile.customerName }}
                  </div>
                  <div class="practice-assistant-form__profile-summary">
                    {{ profile.profile }}
                  </div>
                </div>
                <i
                  class="practice-assistant-form__profile-expand t-iconfont"
                  :class="
                    expandedProfileIds.includes(profile.id)
                      ? 'icon-arrow-up-s-line'
                      : 'icon-arrow-down-s-line'
                  "
                  @click="toggleProfileExpanded(profile.id)"
                ></i>
              </div>

              <div
                v-show="expandedProfileIds.includes(profile.id)"
                class="practice-assistant-form__profile-body"
              >
                <div class="practice-assistant-form__profile-section">
                  <div class="practice-assistant-form__profile-section-title">陪练内容</div>
                  <div class="practice-assistant-form__profile-field">
                    <span class="practice-assistant-form__profile-field-label">场景描述</span>
                    <span class="practice-assistant-form__profile-field-value">
                      {{ profile.background }}
                    </span>
                  </div>
                  <div class="practice-assistant-form__profile-field">
                    <span class="practice-assistant-form__profile-field-label">训练目标</span>
                    <span class="practice-assistant-form__profile-field-value">
                      {{ profile.trainingGoal }}
                    </span>
                  </div>
                </div>
                <div class="practice-assistant-form__profile-section">
                  <div class="practice-assistant-form__profile-section-title">客户信息</div>
                  <div class="practice-assistant-form__profile-field">
                    <span class="practice-assistant-form__profile-field-label">客户原话</span>
                    <span class="practice-assistant-form__profile-field-value">
                      {{ profile.customerQuote }}
                    </span>
                  </div>
                  <div class="practice-assistant-form__profile-field">
                    <span class="practice-assistant-form__profile-field-label">客户初始态度</span>
                    <span class="practice-assistant-form__profile-field-value">
                      {{ profile.initialAttitude }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="practice-assistant-form__step-actions">
            <el-button
              class="practice-assistant-form__step-btn"
              @click="emit('prev-step', 'step-three')"
            >
              返回上一步
            </el-button>
            <el-button
              class="practice-assistant-form__step-btn"
              color="#7a52f4"
              :loading="saveLoading"
              @click="emit('save')"
            >
              {{ saveButtonText }}
            </el-button>
            <p class="practice-assistant-form__step-desc">
              {{ selectedProfileCountText }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import PracticePersonSelect from './person-select/index.vue'
import UploadedFileCard from '@/components/upload/UploadedFileCard.vue'
import { useOSS } from '@/utils/oss'

defineOptions({
  name: 'PracticeAssistantForm'
})

const props = defineProps({
  form: {
    type: Object,
    default: () => ({
      trainingContent: '',
      supplementFile: [],
      themeName: '',
      userRangePojoList: [],
      themeDesc: '',
      dueTime: '',
      supplementSummaryFileUrl: '',
      customerProfileList: [],
      selectedProfileIds: [],
      taskNodeResult: {},
      profileNodeResult: {}
    })
  },
  dialogType: {
    type: String,
    default: 'create'
  },
  activeStep: {
    type: String,
    default: 'step-one'
  },
  extractStatus: {
    type: String,
    default: 'idle'
  },
  profileStatus: {
    type: String,
    default: 'idle'
  },
  saveLoading: {
    type: Boolean,
    default: false
  },
  stepOneExpanded: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits([
  'update:form',
  'extract',
  'generate-profiles',
  'save',
  'toggle-step-one',
  'prev-step'
])

const defaultForm = {
  trainingContent: '',
  supplementFile: [],
  themeName: '',
  userRangePojoList: [],
  themeDesc: '',
  dueTime: '',
  supplementSummaryFileUrl: '',
  customerProfileList: [],
  selectedProfileIds: [],
  taskNodeResult: {},
  profileNodeResult: {}
}

const UPLOAD_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'md', 'markdown']
const UPLOAD_ACCEPT = UPLOAD_EXTENSIONS.map((item) => `.${item}`).join(',')
const UPLOAD_SIZE_MB = 1
const UPLOAD_SIZE_TEXT = `${UPLOAD_SIZE_MB} MB`
const UPLOAD_TEXT = 'PDF、WORD、EXCEL、MARKDOWN'

const uploadLoading = ref(false)
const isDragOver = ref(false)
const uploadInputRef = ref(null)
const expandedProfileIds = ref([])
const { uploadFile } = useOSS()

const updateForm = (patch) => {
  emit('update:form', {
    ...defaultForm,
    ...props.form,
    ...patch
  })
}

const trainingContent = computed({
  get: () => props.form?.trainingContent || '',
  set: (value) => {
    updateForm({ trainingContent: value })
  }
})

const supplementFile = computed({
  get: () => props.form?.supplementFile || [],
  set: (value) => {
    updateForm({ supplementFile: value })
  }
})

const themeName = computed({
  get: () => props.form?.themeName || '',
  set: (value) => {
    updateForm({ themeName: value })
  }
})

const userRangePojoList = computed({
  get: () => props.form?.userRangePojoList || [],
  set: (value) => {
    updateForm({ userRangePojoList: value })
  }
})

const themeDesc = computed({
  get: () => props.form?.themeDesc || '',
  set: (value) => {
    updateForm({ themeDesc: value })
  }
})

const dueTime = computed({
  get: () => props.form?.dueTime || '',
  set: (value) => {
    updateForm({ dueTime: Number(value) })
  }
})

const customerProfileList = computed({
  get: () => props.form?.customerProfileList || [],
  set: (value) => {
    updateForm({ customerProfileList: value })
  }
})

const selectedProfileIds = computed({
  get: () => props.form?.selectedProfileIds || [],
  set: (value) => {
    updateForm({ selectedProfileIds: value })
  }
})

const disabledEndDate = (date) => {
  const todayStart = new Date()
  todayStart.setHours(0, 0, 0, 0)
  return date.getTime() < todayStart.getTime()
}

const isStepOneFinished = computed(() => props.extractStatus !== 'idle')
const isStepTwoFinished = computed(
  () => props.profileStatus !== 'idle' && props.activeStep === 'step-three'
)
const isStepTwoActive = computed(() => props.activeStep === 'step-two')
const isStepThreeActive = computed(() => props.activeStep === 'step-three')
const hasStepTwo = computed(
  () =>
    props.extractStatus === 'loading' ||
    (props.extractStatus === 'done' && (isStepTwoActive.value || isStepThreeActive.value))
)
const showStepOneBody = computed(() => props.extractStatus === 'idle' || props.stepOneExpanded)
const showStepTwoLoading = computed(() => props.extractStatus === 'loading')
const showStepTwoForm = computed(() => props.extractStatus === 'done' && isStepTwoActive.value)
const hasStepThree = computed(() => props.profileStatus !== 'idle')
const showStepThree = computed(() => props.extractStatus === 'done' && isStepThreeActive.value)
const showStepThreeLoading = computed(
  () => props.profileStatus === 'loading' && isStepThreeActive.value
)
const showStepThreeForm = computed(() => props.profileStatus === 'done' && isStepThreeActive.value)

const extractButtonText = computed(() => {
  if (props.extractStatus === 'loading') {
    return 'AI提炼中...'
  }

  return props.extractStatus === 'done' ? '重新提炼' : 'AI 提炼'
})

const saveButtonText = computed(() => {
  return props.dialogType === 'edit' ? '保存并派发' : '确定派发'
})

const generateProfilesButtonText = computed(() => {
  if (props.profileStatus === 'loading') {
    return '生成中...'
  }

  return props.profileStatus === 'done' ? '重新生成客户画像' : '生成客户画像'
})

const selectedProfileCountText = computed(() => {
  return `已选（${selectedProfileIds.value.length}/${customerProfileList.value.length}）`
})

watch(
  () => [props.profileStatus, customerProfileList.value],
  ([profileStatusValue, currentProfileList]) => {
    if (profileStatusValue !== 'done' || !currentProfileList.length) {
      expandedProfileIds.value = []
      return
    }

    expandedProfileIds.value = [currentProfileList[0].id]
  },
  {
    immediate: true,
    deep: true
  }
)

const getFileExt = (fileName = '') => {
  const lastDotIndex = fileName.lastIndexOf('.')
  return lastDotIndex > -1 ? fileName.slice(lastDotIndex + 1).toLowerCase() : ''
}

const getCurrentTotalSize = () => {
  return supplementFile.value.reduce((total, file) => total + (file.size || 0), 0)
}

const validateUploadFile = (file, selectedTotalSize = 0) => {
  const ext = getFileExt(file.name)

  if (!UPLOAD_EXTENSIONS.includes(ext)) {
    return `仅支持 ${UPLOAD_TEXT} 格式`
  }

  if (getCurrentTotalSize() + selectedTotalSize + file.size > UPLOAD_SIZE_MB * 1024 * 1024) {
    return `总文件大小不能超过 ${UPLOAD_SIZE_TEXT}`
  }

  return ''
}

const createUploadingFile = (rawFile) => ({
  id: `upload-file-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  fileName: rawFile.name,
  filename: rawFile.name,
  size: rawFile.size,
  suffix: getFileExt(rawFile.name),
  ext: getFileExt(rawFile.name)
})

const isProfileSelected = (profileId) => {
  return selectedProfileIds.value.includes(String(profileId))
}

const toggleProfileExpanded = (profileId) => {
  const normalizedProfileId = String(profileId || '')

  if (!normalizedProfileId) {
    return
  }

  if (expandedProfileIds.value.includes(normalizedProfileId)) {
    expandedProfileIds.value = expandedProfileIds.value.filter(
      (item) => item !== normalizedProfileId
    )
    return
  }

  expandedProfileIds.value = [...expandedProfileIds.value, normalizedProfileId]
}

const handleProfileSelectionChange = (profile, checked) => {
  const profileId = String(profile?.id || '')

  if (!profileId) {
    return
  }

  const nextSelectedIds = checked
    ? Array.from(new Set([...selectedProfileIds.value, profileId]))
    : selectedProfileIds.value.filter((item) => item !== profileId)

  if (!nextSelectedIds.length) {
    ElMessage.warning('至少保留 1 个客户画像')
    return
  }

  selectedProfileIds.value = nextSelectedIds
}

const uploadSingleFile = async (fileItem, rawFile) => {
  try {
    const attachIndex = await uploadFile(rawFile)
    supplementFile.value = supplementFile.value.map((item) =>
      item.id === fileItem.id
        ? {
            url: attachIndex,
            attachIndex,
            fileName: item.fileName,
            filename: item.filename,
            size: item.size,
            suffix: item.suffix,
            ext: item.ext
          }
        : item
    )
  } catch (error) {
    supplementFile.value = supplementFile.value.filter((item) => item.id !== fileItem.id)
    ElMessage.error(error?.message || '文件上传失败，请稍后重试')
  }
}

const processRawFiles = async (rawFiles) => {
  if (!rawFiles.length) {
    return
  }

  const validFiles = []
  let selectedTotalSize = 0

  rawFiles.forEach((rawFile) => {
    const errorMessage = validateUploadFile(rawFile, selectedTotalSize)
    if (errorMessage) {
      ElMessage.error(errorMessage)
      return
    }

    validFiles.push(rawFile)
    selectedTotalSize += rawFile.size
  })

  if (!validFiles.length) {
    return
  }

  uploadLoading.value = true
  const uploadItems = validFiles.map(createUploadingFile)
  supplementFile.value = [...supplementFile.value, ...uploadItems]

  try {
    await Promise.all(
      uploadItems.map((fileItem, index) => uploadSingleFile(fileItem, validFiles[index]))
    )
    await nextTick()
  } finally {
    uploadLoading.value = false
  }
}

const handleUploadInputChange = async (event) => {
  await processRawFiles(Array.from(event.target.files || []))
  event.target.value = ''
}

const handleDeleteUploadedFile = (file) => {
  supplementFile.value = supplementFile.value.filter(
    (item) => item.attachIndex !== file.attachIndex
  )
}

const handleClickUpload = () => {
  if (uploadLoading.value || props.extractStatus === 'loading') {
    return
  }

  uploadInputRef.value?.click()
}

const handleDragOver = () => {
  if (uploadLoading.value || props.extractStatus === 'loading') {
    return
  }

  isDragOver.value = true
}

const handleDragLeave = () => {
  isDragOver.value = false
}

const handleDrop = async (event) => {
  isDragOver.value = false

  if (uploadLoading.value || props.extractStatus === 'loading') {
    return
  }

  await processRawFiles(Array.from(event.dataTransfer?.files || []))
}

const handleExtractClick = () => {
  if (uploadLoading.value) {
    ElMessage.warning('文件上传中，请稍后提炼')
    return
  }

  if (props.extractStatus === 'loading') {
    return
  }

  emit('extract')
}
</script>

<style lang="scss" scoped>
// 文字/Text-4 强调正文标题
$text-strong: #323233;
// 文字/Text-3 次强调正文标题
$text-secondary: #646566;
// 文字/Text-2 次要信息
$text-placeholder: #969799;
// 线条/Line-1 浅线条
$line-light: #f2f3f5;
// 线条/Line-2 一般线条
$line-normal: #e5e6eb;
// 拓展色/紫罗兰/Violets-6
$violet-6: #7a52f4;
// 填充/纯白
$fill-white: #ffffff;
// 填充/Fill-1 浅色 禁用背景
$fill-soft: #f7f8fa;
// 成功色/Success-6 常规
$success-6: #00c241;
// 错误色/Error-6 常规
$error-6: #f54e4e;

.practice-assistant-form {
  padding-bottom: 24px;

  &__step {
    display: flex;
    gap: 8px;
  }

  &__step + &__step {
    margin-top: 12px;
  }

  &__step-side {
    width: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
  }

  &__step-node {
    width: 20px;
    height: 20px;
    border: 1px solid $line-normal;
    border-radius: 50%;
    background: $fill-soft;
    color: $text-placeholder;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    box-sizing: border-box;

    &.is-current {
      border-color: $text-strong;
      color: $text-strong;
    }

    &.is-done {
      border: 0;
      background: transparent;
      color: $success-6;
      font-size: 20px;
      line-height: 20px;
    }
  }

  &__step-line {
    width: 1px;
    flex: 1;
    margin-top: 8px;
    background: $line-light;
  }

  &__step-main {
    flex: 1;
    min-width: 0;
  }

  &__step-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
  }

  &__step-title {
    font-size: 16px;
    line-height: 22px;
    color: $text-placeholder;

    &.is-current {
      font-weight: 600;
      color: $text-strong;
    }
  }

  &__step-toggle {
    flex-shrink: 0;
    font-size: 20px;
    line-height: 20px;
    color: $text-placeholder;
    cursor: pointer;
  }

  &__step-body {
    margin-top: 8px;
  }

  &__step-loading {
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    line-height: 18px;
    color: $text-placeholder;
  }

  &__loading-icon {
    font-size: 14px;
    line-height: 14px;
    animation: practice-assistant-form-spin 1s linear infinite;
  }

  &__field + &__field {
    margin-top: 16px;
  }

  &__field-label {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: $text-strong;
  }

  &__required {
    margin-right: 4px;
    color: $error-6;
  }

  &__field-content {
    width: 100%;
  }

  &__field-content--half {
    width: 480px;
    max-width: 100%;
  }

  &__date-picker {
    width: 100%;
  }

  &__upload-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__upload-box {
    min-height: 68px;
    padding: 12px;
    border: 1px dashed $line-normal;
    border-radius: 16px;
    background: $fill-soft;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &.is-disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }

    &.is-dragover {
      border-color: $violet-6;
      background: rgba(114, 46, 209, 0.04);
    }
  }

  &__upload-action {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    line-height: 20px;
    color: $text-strong;

    .t-iconfont {
      font-size: 18px;
      line-height: 18px;
    }
  }

  &__upload-hint {
    margin-top: 4px;
    font-size: 14px;
    line-height: 20px;
    color: $text-placeholder;
    text-align: center;
  }

  &__upload-tip {
    margin-top: 4px;
    font-size: 12px;
    line-height: 16px;
    color: $text-placeholder;
    margin-bottom: 16px;
  }

  &__step-actions {
    display: flex;
    align-items: center;
    // gap: 12px;
    margin-top: 16px;
  }

  &__step-btn {
    border-radius: 12px;
  }

  &__step-desc {
    flex: 1;
    min-width: 0;
    margin-left: 12px;
    font-size: 12px;
    line-height: 16px;
    color: #969799;
  }

  &__step-tips {
    margin-bottom: 12px;
    font-size: 14px;
    line-height: 20px;
    color: $text-placeholder;
  }

  &__profile-list {
    max-height: 460px;
    overflow-y: auto;
  }

  &__profile-card {
    border: 1px solid $line-normal;
    border-radius: 12px;
    background: $fill-white;
    overflow: hidden;

    & + & {
      margin-top: 12px;
    }

    &.is-selected {
      border-color: rgba(114, 46, 209, 0.4);
    }
  }

  &__profile-header {
    display: flex;
    align-items: center;
    padding: 12px;
  }

  &__profile-info {
    flex: 1;
    min-width: 0;
    margin: 0 12px;
  }

  &__profile-name {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: $text-strong;
  }

  &__profile-summary {
    margin-top: 4px;
    font-size: 12px;
    line-height: 16px;
    color: $text-placeholder;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__profile-expand {
    flex-shrink: 0;
    font-size: 20px;
    line-height: 20px;
    color: $text-secondary;
    cursor: pointer;
  }

  &__profile-body {
    padding: 0 12px 12px 40px;
  }

  &__profile-section {
    padding: 12px;
    border-radius: 16px;
    background: $fill-soft;

    & + & {
      margin-top: 16px;
    }
  }

  &__profile-section-title {
    position: relative;
    padding-left: 12px;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: $text-strong;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 3px;
      width: 4px;
      height: 14px;
      border-radius: 4px;
      background: $violet-6;
    }
  }

  &__profile-field {
    padding: 8px 12px;
    border-radius: 12px;
    background: $fill-white;

    & + & {
      margin-top: 8px;
    }
  }

  &__profile-field-label {
    display: block;
    margin-bottom: 4px;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    color: $text-strong;
  }

  &__profile-field-value {
    display: block;
    font-size: 13px;
    line-height: 18px;
    color: $text-secondary;
  }

  &__textarea--prompt {
    :deep(.el-textarea__inner) {
      min-height: 340px !important;
      padding: 16px 20px;
      border-radius: 16px;
    }
  }

  &__textarea--summary {
    :deep(.el-textarea__inner) {
      min-height: 180px !important;
      padding: 16px 20px;
      border-radius: 16px;
    }
  }

  :deep(.el-input__wrapper) {
    border-radius: 8px;
  }
}

@keyframes practice-assistant-form-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
